import React, { useState } from "react";
import { useCasesApi } from "../hooks/useCasesApi";


// Helper function to combine first and last name
const combineNames = (firstName: string, lastName: string): string => {
  const first = firstName || '';
  const last = lastName || '';
  
  if (!first && !last) return '';
  if (!last) return first;
  if (!first) return last;
  return `${first} ${last}`;
};


interface NewCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface NewCaseFormData {
  clientName: string; // Keep for backward compatibility
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone: string;
  caseTitle: string;
  caseDescription: string;
  country: string;
  caseType: string;
  priority: string;
}

export default function NewCaseModal({ isOpen, onClose, onSuccess }: NewCaseModalProps) {
  const { createCase } = useCasesApi();
  const [formData, setFormData] = useState<NewCaseFormData>({
    clientName: "", // Keep for backward compatibility
    clientFirstName: "",
    clientLastName: "",
    clientEmail: "",
    clientPhone: "",
    caseTitle: "",
    caseDescription: "",
    country: "USA",
    caseType: "Family Visa (I-130)",
    priority: "medium",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Case types by country
  const caseTypesByCountry = {
    USA: [
      "Family Visa (I-130)",
      "Work Visa (H-1B)",
      "Student Visa (F-1)",
      "Business Visa (E-2)",
      "Green Card (I-485)",
      "Citizenship (N-400)",
      "Asylum (I-589)",
      "Other (USA)",
    ],
    Canada: [
      "Study Permit",
      "Work Permit",
      "Visitor Visa",
      "Business Immigration",
      "Citizenship",
      "Refugee Claim",
      "Other (Canada)",
    ],
  };

  const handleInputChange = (field: keyof NewCaseFormData, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Reset case type when country changes
      if (field === "country") {
        const availableCaseTypes =
          caseTypesByCountry[value as keyof typeof caseTypesByCountry];
        newData.caseType = availableCaseTypes[0];
      }

      return newData;
    });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields (only client first name and email are required now)
      if (
        !formData.clientFirstName.trim() ||
        !formData.clientEmail.trim()
      ) {
        throw new Error("Please fill in all required fields (Client First Name and Email)");
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.clientEmail)) {
        throw new Error("Please enter a valid email address");
      }

      await createCase({
        clientName: combineNames(formData.clientFirstName, formData.clientLastName), // Keep for backward compatibility
        clientFirstName: formData.clientFirstName,
        clientLastName: formData.clientLastName,
        clientEmail: formData.clientEmail,
        title: formData.caseTitle || `Case for ${combineNames(formData.clientFirstName, formData.clientLastName)}`, // Default title if empty
        description: formData.caseDescription,
        caseType: formData.caseType || (formData.country === 'Canada' ? 'Other (Canada)' : 'Other (USA)'), // Country-specific default
        country: formData.country || "USA", // Default country if empty
        ...(formData.priority ? { priority: formData.priority } : {}),
      });

      setSuccess(true);
      // Reset form
      setFormData({
        clientName: "", // Keep for backward compatibility
        clientFirstName: "",
        clientLastName: "",
        clientEmail: "",
        clientPhone: "",
        caseTitle: "",
        caseDescription: "",
        country: "USA",
        caseType: "Family Visa (I-130)",
        priority: "",
      });
      
      // Call success callback and close modal after a short delay
      setTimeout(() => {
        onSuccess?.();
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error("Error in NewCase form:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === "string"
          ? err
          : "Failed to create case";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
      setSuccess(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Case</h2>
          <button 
            className="close-btn"
            onClick={handleClose}
            disabled={loading}
            title="Close (Esc)"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {success ? (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h3>Case Created Successfully!</h3>
              <p>Your new case has been created and added to your dashboard.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="new-case-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-section">
                <h3>Client Information</h3>

                <div className="form-group">
                  <label htmlFor="clientFirstName">First Name *</label>
                  <input
                    type="text"
                    id="clientFirstName"
                    value={formData.clientFirstName}
                    onChange={(e) => handleInputChange("clientFirstName", e.target.value)}
                    placeholder="Enter client's first name"
                    required
                    disabled={loading}
                    maxLength={15}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="clientLastName">Last Name</label>
                  <input
                    type="text"
                    id="clientLastName"
                    value={formData.clientLastName}
                    onChange={(e) => handleInputChange("clientLastName", e.target.value)}
                    placeholder="Enter client's last name (optional)"
                    disabled={loading}
                    maxLength={15}
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                    Leave empty or enter "N/A" if no last name
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="clientEmail">Client Email *</label>
                  <input
                    type="email"
                    id="clientEmail"
                    value={formData.clientEmail}
                    onChange={(e) => handleInputChange("clientEmail", e.target.value)}
                    placeholder="Enter client's email address"
                    required
                    disabled={loading}
                    maxLength={25}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="clientPhone">Client Phone</label>
                  <input
                    type="tel"
                    id="clientPhone"
                    value={formData.clientPhone}
                    onChange={(e) => handleInputChange("clientPhone", e.target.value)}
                    placeholder="Enter client's phone number"
                    disabled={loading}
                    maxLength={10}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Case Details</h3>

                <div className="form-group">
                  <label htmlFor="caseTitle">Case Title</label>
                  <input
                    type="text"
                    id="caseTitle"
                    value={formData.caseTitle}
                    onChange={(e) => handleInputChange("caseTitle", e.target.value)}
                    placeholder="Enter a descriptive title for the case"
                    disabled={loading}
                    maxLength={25}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <select
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleInputChange("country", e.target.value)}
                    disabled={loading}
                  >
                    <option value="USA">United States</option>
                    <option value="Canada">Canada</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="caseType">Case Type</label>
                  <select
                    id="caseType"
                    value={formData.caseType}
                    onChange={(e) => handleInputChange("caseType", e.target.value)}
                    disabled={loading}
                  >
                    {caseTypesByCountry[
                      formData.country as keyof typeof caseTypesByCountry
                    ]?.map((caseType) => (
                      <option key={caseType} value={caseType}>
                        {caseType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="priority">Priority</label>
                  <select
                    id="priority"
                    value={formData.priority}
                    onChange={(e) => handleInputChange("priority", e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Select priority</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="caseDescription">Case Description</label>
                  <textarea
                    id="caseDescription"
                    value={formData.caseDescription}
                    onChange={(e) =>
                      handleInputChange("caseDescription", e.target.value)
                    }
                    placeholder="Provide details about the case, requirements, and any special considerations"
                    rows={3}
                    disabled={loading}
                    maxLength={100}
                  />
                </div>
              </div>
            </form>
          )}
        </div>

        {!success && (
          <div className="modal-footer">
            <button 
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Creating Case..." : "Create Case"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 